import React, { useEffect } from 'react';
import styled from 'styled-components';
import { FiPlus, FiTrash2, FiVideo } from 'react-icons/fi';

const HelpYouPlanForm = ({ helpYouPlan, onChange, onVideoFileChange, videoPreviews = {} }) => {
  
  useEffect(() => {
    console.log('HelpYouPlanForm received helpYouPlan:', helpYouPlan);
  }, [helpYouPlan]);

  // Handle checkbox toggle
  const handleEnabledToggle = (checked) => {
    onChange('helpYouPlan.enabled', checked);
  };

  // Handle section heading
  const handleSectionHeadingChange = (value) => {
    onChange('helpYouPlan.sectionHeading', value);
  };

  // Handle card updates
  const handleCardChange = (index, field, value) => {
    const updatedCards = [...(helpYouPlan.cards || [])];
    updatedCards[index] = {
      ...updatedCards[index],
      [field]: value
    };
    console.log('Updating card', index, field, value);
    onChange('helpYouPlan.cards', updatedCards);
  };

  // Add new card
  const addCard = () => {
    const updatedCards = [...(helpYouPlan.cards || []), {
      cardName: '',
      cardDescription: '',
      videoUrl: '',
      videoTitle: '',
      videoSubtitle: ''
    }];
    console.log('Adding card. Updated cards:', updatedCards);
    onChange('helpYouPlan.cards', updatedCards);
  };

  // Remove card
  const removeCard = (index) => {
    const updatedCards = (helpYouPlan.cards || []).filter((_, i) => i !== index);
    onChange('helpYouPlan.cards', updatedCards);
  };

  return (
    <HelpPlanContainer>
      <SectionHeader>
        <SectionTitle>What We Can Help You Plan Section (Optional)</SectionTitle>
        <EnableCheckbox>
          <input
            type="checkbox"
            checked={helpYouPlan.enabled || false}
            onChange={(e) => handleEnabledToggle(e.target.checked)}
            id="helpYouPlanEnabled"
          />
          <label htmlFor="helpYouPlanEnabled">
            Enable This Section
          </label>
        </EnableCheckbox>
      </SectionHeader>

      <FormDescription>
        Create interactive video cards. When a card is clicked, the main video and text content change.
        Perfect for showcasing different catering options, venue types, or service packages.
      </FormDescription>

      {/* Section Heading */}
      <SubSection>
        <SubSectionTitle>Section Heading</SubSectionTitle>
        <FormGroup>
          <Label>Overlay Heading</Label>
          <Input
            type="text"
            placeholder="What We Can Help You Plan"
            value={helpYouPlan.sectionHeading || ''}
            onChange={(e) => handleSectionHeadingChange(e.target.value)}
          />
          <Hint>This heading appears as an overlay on the video section</Hint>
        </FormGroup>
      </SubSection>

      {/* Video Cards */}
      <SubSection>
        <SubSectionTitle>
          Video Cards ({(helpYouPlan.cards || []).length})
          <AddCardBtn onClick={addCard}>
            <FiPlus size={14} /> Add Video Card
          </AddCardBtn>
        </SubSectionTitle>

        {(helpYouPlan.cards || []).map((card, index) => (
          <CardItem key={index}>
            <CardHeader>
              <CardNumber>
                <FiVideo size={18} /> Card {index + 1}
                {index === 0 && <DefaultBadge>Default Active</DefaultBadge>}
              </CardNumber>
              {(helpYouPlan.cards || []).length > 1 && (
                <RemoveCardBtn onClick={() => removeCard(index)}>
                  <FiTrash2 size={14} /> Remove
                </RemoveCardBtn>
              )}
            </CardHeader>

            <CardSection>
              <SectionLabel>Card Display (Bottom Grid)</SectionLabel>
              <CardGrid>
                <FormGroup>
                  <Label>Card Name *</Label>
                  <Input
                    type="text"
                    placeholder="e.g., Italian, Asian Fusion, BBQ"
                    value={card.cardName || ''}
                    onChange={(e) => handleCardChange(index, 'cardName', e.target.value)}
                  />
                  <Hint>Short name shown on card</Hint>
                </FormGroup>

                <FormGroup>
                  <Label>Card Description *</Label>
                  <Input
                    type="text"
                    placeholder="e.g., Fresh pasta, wood-fired pizza..."
                    value={card.cardDescription || ''}
                    onChange={(e) => handleCardChange(index, 'cardDescription', e.target.value)}
                  />
                  <Hint>Brief description shown on card</Hint>
                </FormGroup>
              </CardGrid>
            </CardSection>

            <CardSection>
              <SectionLabel>Video & Content (Changes When Card is Clicked)</SectionLabel>
              
              <FormGroup>
                <Label>Video URL</Label>
                <VideoInput
                  type="url"
                  placeholder="https://example.com/video.mp4"
                  value={card.videoUrl || ''}
                  onChange={(e) => handleCardChange(index, 'videoUrl', e.target.value)}
                />
                <Hint>
                  Paste a direct MP4 video URL or upload a file below
                  {card.videoUrl && (
                    <VideoPreviewLink href={card.videoUrl} target="_blank" rel="noopener">
                      Test Video
                    </VideoPreviewLink>
                  )}
                </Hint>
              </FormGroup>

              <FormGroup>
                <Label>Or Upload Video File *</Label>
                <FileInputLabel htmlFor={`videoFile${index}`}>
                  <FiVideo style={{ marginRight: '5px' }} />
                  Choose Video File
                </FileInputLabel>
                <FileInput
                  id={`videoFile${index}`}
                  type="file"
                  accept="video/mp4,video/webm,video/ogg"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      console.log('Video file selected:', file.name);
                      if (onVideoFileChange) {
                        onVideoFileChange(index, file);
                      }
                    }
                  }}
                />
                {card.videoFileName && (
                  <SelectedFileInfo>
                    Selected: {card.videoFileName}
                  </SelectedFileInfo>
                )}
                {videoPreviews[index] && (
                  <VideoPreviewContainer>
                    <VideoPreviewLabel>Video Preview:</VideoPreviewLabel>
                    <VideoPreviewElement controls>
                      <source src={videoPreviews[index]} type="video/mp4" />
                      Your browser does not support the video tag.
                    </VideoPreviewElement>
                  </VideoPreviewContainer>
                )}
                <Hint>Max size: 100MB. Formats: MP4, WebM, OGG</Hint>
              </FormGroup>

              <FormGroup>
                <Label>Video Title *</Label>
                <Input
                  type="text"
                  placeholder="e.g., Authentic Italian Cuisine"
                  value={card.videoTitle || ''}
                  onChange={(e) => handleCardChange(index, 'videoTitle', e.target.value)}
                />
                <Hint>Main heading displayed when this card is active</Hint>
              </FormGroup>

              <FormGroup>
                <Label>Video Subtitle/Description *</Label>
                <Textarea
                  placeholder="Detailed description that appears when this card is clicked..."
                  value={card.videoSubtitle || ''}
                  onChange={(e) => handleCardChange(index, 'videoSubtitle', e.target.value)}
                  rows={3}
                />
                <Hint>Paragraph text displayed with the video</Hint>
              </FormGroup>
            </CardSection>

            {/* Card Preview */}
            <PreviewBox>
              <PreviewLabel>Preview:</PreviewLabel>
              <PreviewCardMini>
                <PreviewCardName>{card.cardName || 'Card Name'}</PreviewCardName>
                <PreviewCardDesc>{card.cardDescription || 'Description'}</PreviewCardDesc>
              </PreviewCardMini>
              <PreviewArrow>→ When clicked shows:</PreviewArrow>
              <PreviewContent>
                <PreviewTitle>{card.videoTitle || 'Title'}</PreviewTitle>
                <PreviewSubtitle>{card.videoSubtitle || 'Subtitle/Description'}</PreviewSubtitle>
              </PreviewContent>
            </PreviewBox>
          </CardItem>
        ))}

        {(!helpYouPlan.cards || helpYouPlan.cards.length === 0) && (
          <EmptyState onClick={addCard}>
            <FiVideo size={32} color="#ccc" />
            <p>Click to add your first video card</p>
            <EmptyHint>Great for showcasing catering options, venues, or service types</EmptyHint>
          </EmptyState>
        )}
      </SubSection>

      {/* Usage Tips */}
      <TipsSection>
        <TipsTitle>💡 Tips for Best Results</TipsTitle>
        <TipsList>
          <li><strong>First card is default:</strong> The first card's video/content shows when page loads</li>
          <li><strong>Video format:</strong> Use MP4 format for best compatibility across devices</li>
          <li><strong>Video hosting:</strong> Host videos on cloud storage (Google Cloud, AWS S3) or use Vimeo direct links</li>
          <li><strong>Keep cards concise:</strong> Card names should be 1-3 words, descriptions 5-10 words</li>
          <li><strong>Video titles:</strong> Make them engaging and descriptive (3-5 words)</li>
          <li><strong>Video subtitles:</strong> 1-2 sentences that explain the offering</li>
        </TipsList>
      </TipsSection>
    </HelpPlanContainer>
  );
};

// Styled Components
const HelpPlanContainer = styled.div`
  background: #f8f9fa;
  border-radius: 12px;
  padding: 24px;
  margin-top: 24px;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const EnableCheckbox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }

  label {
    font-size: 14px;
    font-weight: 500;
    color: #555;
    cursor: pointer;
  }
`;

const FormDescription = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 20px;
  line-height: 1.6;
`;

const SubSection = styled.div`
  background: white;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 16px;
`;

const SubSectionTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #4CAF50;
  }
`;

const VideoInput = styled(Input)`
  font-family: 'Monaco', 'Courier New', monospace;
  font-size: 13px;
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #4CAF50;
  }
`;

const Hint = styled.div`
  font-size: 12px;
  color: #666;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const VideoPreviewLink = styled.a`
  color: #4CAF50;
  text-decoration: none;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
`;

const FileInputLabel = styled.label`
  display: inline-flex;
  align-items: center;
  padding: 10px 16px;
  background: #4CAF50;
  color: white;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #45a049;
  }
`;

const FileInput = styled.input`
  display: none;
`;

const SelectedFileInfo = styled.div`
  margin-top: 8px;
  padding: 8px 12px;
  background: #e8f5e9;
  border-radius: 4px;
  font-size: 13px;
  color: #2e7d32;
  border-left: 3px solid #4CAF50;
`;

const AddCardBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #45a049;
  }
`;

const CardItem = styled.div`
  background: #f8f9fa;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 16px;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid #ddd;
`;

const CardNumber = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  color: #333;
`;

const DefaultBadge = styled.span`
  background: #4CAF50;
  color: white;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 10px;
  font-weight: 500;
`;

const RemoveCardBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: #ff6b6b;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #ff5252;
  }
`;

const CardSection = styled.div`
  background: white;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 12px;
`;

const SectionLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #555;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PreviewBox = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 8px;
  padding: 16px;
  color: white;
`;

const PreviewLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  opacity: 0.9;
  margin-bottom: 12px;
`;

const PreviewCardMini = styled.div`
  background: rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  padding: 10px;
  margin-bottom: 12px;
`;

const PreviewCardName = styled.div`
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 4px;
`;

const PreviewCardDesc = styled.div`
  font-size: 12px;
  opacity: 0.9;
`;

const PreviewArrow = styled.div`
  text-align: center;
  font-size: 12px;
  margin: 8px 0;
  opacity: 0.8;
`;

const PreviewContent = styled.div`
  background: rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  padding: 12px;
`;

const PreviewTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
`;

const PreviewSubtitle = styled.div`
  font-size: 13px;
  line-height: 1.5;
  opacity: 0.95;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 50px 20px;
  color: #999;
  cursor: pointer;
  border: 2px dashed #ddd;
  border-radius: 8px;
  transition: all 0.2s;

  &:hover {
    border-color: #4CAF50;
    color: #4CAF50;
  }

  p {
    margin-top: 12px;
    font-size: 15px;
    font-weight: 500;
  }
`;

const EmptyHint = styled.div`
  font-size: 13px;
  color: #aaa;
  margin-top: 8px;
`;

const TipsSection = styled.div`
  background: #fff9e6;
  border-left: 4px solid #ffc107;
  border-radius: 6px;
  padding: 16px;
`;

const TipsTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
`;

const TipsList = styled.ul`
  margin: 0;
  padding-left: 20px;
  
  li {
    font-size: 13px;
    color: #666;
    line-height: 1.6;
    margin-bottom: 8px;
    
    strong {
      color: #333;
    }
  }
`;

const VideoPreviewContainer = styled.div`
  margin-top: 12px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 2px solid #4CAF50;
`;

const VideoPreviewLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
`;

const VideoPreviewElement = styled.video`
  width: 100%;
  max-height: 300px;
  border-radius: 6px;
  background: #000;
`;

export default HelpYouPlanForm;
